/**
 * Contractor profile page editor
 *
 * The contractor's public page: company details, the team, and past work.
 * Publishing saves first, so what goes live is what is on screen.
 */

import {
	ArrowDown,
	ArrowUp,
	Copy,
	ExternalLink,
	Plus,
	Rocket,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	MultiImageUpload,
	SingleImageUpload,
} from "@/components/ui/image-upload";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { TemplateSwitcher } from "@/features/portfolio/components/template-switcher";
import type { PortfolioTemplate } from "@/features/portfolio/services/portfolio-api.service";
import {
	getPortfolioTemplate,
	normalizeContractorTemplate,
	renderPublicTemplateHeader,
	TemplateDeltaNotice,
} from "@/features/portfolio/template-definitions";
import {
	Chip,
	FloatPanel,
	PageHeader,
	Pill,
	StudioButton,
} from "@/features/studio/theme";
import { TRADES, useServiceCities } from "@/lib/constants/markets";
import { titleCase } from "@/lib/format";
import {
	type ContractorCompany,
	type ContractorProfileProject,
	type ContractorProfileTemplate,
	type ContractorTeamMember,
	getMyContractorProfile,
	publishMyContractorProfile,
	saveMyContractorProfile,
	unpublishMyContractorProfile,
} from "../services/contractor-profile-api.service";

interface EditableTeamMember extends ContractorTeamMember {
	clientKey: string;
}

interface EditableProject extends ContractorProfileProject {
	clientKey: string;
}

// A plain counter, not crypto.randomUUID(): that API only exists in a secure
// context, so the page crashed on load whenever it was served over plain http.
let nextClientKey = 0;
function createClientKey(prefix: string): string {
	nextClientKey += 1;
	return `${prefix}-${nextClientKey}`;
}

function emptyCompany(): ContractorCompany {
	return {
		name: "",
		logoUrl: "",
		description: "",
		experienceYears: undefined,
		serviceAreas: [],
		specialisations: [],
	};
}

function createEditableProject(
	project?: ContractorProfileProject,
): EditableProject {
	return {
		title: "",
		shortDescription: "",
		thumbnailUrl: "",
		imageUrls: [],
		...project,
		clientKey: project?.id ?? createClientKey("project"),
	};
}

function createEditableTeamMember(
	member?: ContractorTeamMember,
): EditableTeamMember {
	return {
		name: "",
		role: "",
		experienceYears: undefined,
		shortBio: "",
		profilePhotoUrl: "",
		...member,
		clientKey: member?.id ?? createClientKey("member"),
	};
}

function isBlankMember(member: ContractorTeamMember): boolean {
	return (
		!member.name?.trim() &&
		!member.role?.trim() &&
		!member.shortBio?.trim() &&
		!member.profilePhotoUrl &&
		!member.experienceYears
	);
}

function isBlankProject(project: ContractorProfileProject): boolean {
	return (
		!project.title?.trim() &&
		!project.shortDescription?.trim() &&
		(project.imageUrls?.length ?? 0) === 0
	);
}

/** A non-negative whole number from a number input, or undefined when cleared. */
function parseYears(raw: string): number | undefined | null {
	if (raw === "") return undefined;
	const value = Number(raw);
	return Number.isInteger(value) && value >= 0 ? value : null;
}

const INPUT_CLASS =
	"h-10 w-full rounded-[7px] border border-dashboard-border bg-dashboard-warm-450 px-3.5 font-body text-sm text-dashboard-text outline-none transition-shadow placeholder:text-dashboard-placeholder focus:border-dashboard-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)]";
const TEXTAREA_CLASS =
	"min-h-[88px] w-full rounded-[7px] border border-dashboard-border bg-dashboard-warm-450 px-3.5 py-2.5 font-body text-sm leading-relaxed text-dashboard-text outline-none transition-shadow placeholder:text-dashboard-placeholder focus:border-dashboard-accent focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-dashboard-accent)_12%,transparent)]";

function Field({
	label,
	htmlFor,
	hint,
	children,
	className,
}: {
	label: string;
	htmlFor?: string;
	hint?: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div className={`space-y-1.5 ${className ?? ""}`}>
			<label
				htmlFor={htmlFor}
				className="block font-body text-sm font-medium text-dashboard-text"
			>
				{label}
			</label>
			{hint ? (
				<p className="font-body text-[13px] text-dashboard-muted">{hint}</p>
			) : null}
			{children}
		</div>
	);
}

function YearsInput({
	id,
	value,
	onChange,
}: {
	id: string;
	value: number | undefined;
	onChange: (years: number | undefined) => void;
}) {
	return (
		<input
			id={id}
			type="number"
			inputMode="numeric"
			min={0}
			step={1}
			value={value ?? ""}
			onKeyDown={(e) => {
				if (e.key === "-" || e.key === "." || e.key === "e") e.preventDefault();
			}}
			onChange={(e) => {
				const years = parseYears(e.target.value);
				if (years !== null) onChange(years);
			}}
			className={INPUT_CLASS}
		/>
	);
}

/**
 * Toggle chips for a known list, plus a field for anything the list lacks.
 * Values that are selected but not in the list (typed earlier) stay visible
 * as chips so they can be removed.
 */
function ChipPicker({
	legend,
	options,
	selected,
	onChange,
	addPlaceholder,
	normalise = (value) => value.trim(),
}: {
	legend: string;
	options: readonly { value: string; label: string }[];
	selected: string[];
	onChange: (next: string[]) => void;
	addPlaceholder: string;
	normalise?: (value: string) => string;
}) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState("");
	const extras = selected
		.filter((value) => !options.some((option) => option.value === value))
		.map((value) => ({ value, label: value }));
	const all = [...options, ...extras];

	const toggle = (value: string) =>
		onChange(
			selected.includes(value)
				? selected.filter((v) => v !== value)
				: [...selected, value],
		);

	const add = () => {
		const value = normalise(draft);
		if (!value) return;
		const existing = all.find(
			(option) =>
				option.value.toLowerCase() === value.toLowerCase() ||
				option.label.toLowerCase() === value.toLowerCase(),
		);
		const canonical = existing?.value ?? value;
		if (!selected.includes(canonical)) onChange([...selected, canonical]);
		setDraft("");
	};

	return (
		<fieldset className="space-y-3">
			<legend className="mb-1.5 font-body text-sm font-medium text-dashboard-text">
				{legend}
			</legend>
			<div className="flex flex-wrap gap-2">
				{all.map((option) => (
					<Chip
						key={option.value}
						active={selected.includes(option.value)}
						onClick={() => toggle(option.value)}
					>
						{option.label}
					</Chip>
				))}
			</div>
			<div className="flex max-w-md gap-2">
				<input
					type="text"
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							add();
						}
					}}
					placeholder={addPlaceholder}
					aria-label={addPlaceholder}
					className={INPUT_CLASS}
				/>
				<StudioButton
					variant="secondary"
					size="sm"
					onClick={add}
					disabled={!draft.trim()}
					style={{ height: 40 }}
				>
					{t("contractorProfile.add")}
				</StudioButton>
			</div>
		</fieldset>
	);
}

function SectionPanel({
	title,
	description,
	action,
	children,
}: {
	title: string;
	description?: string;
	action?: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<FloatPanel elevation={1} padding={0}>
			<section>
				<div className="flex flex-col gap-3 border-b border-dashboard-divider px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-7">
					<div>
						<h2 className="font-display text-xl font-medium text-dashboard-text">
							{title}
						</h2>
						{description ? (
							<p className="mt-1 max-w-[60ch] font-body text-[13px] leading-relaxed text-dashboard-muted">
								{description}
							</p>
						) : null}
					</div>
					{action}
				</div>
				<div className="px-5 py-6 sm:px-7">{children}</div>
			</section>
		</FloatPanel>
	);
}

export function ManageContractorProfilePage(): React.ReactElement {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [saving, setSaving] = useState(false);
	const [publishing, setPublishing] = useState(false);

	const [company, setCompany] = useState<ContractorCompany>(emptyCompany());
	const [teamMembers, setTeamMembers] = useState<EditableTeamMember[]>([]);
	const [projects, setProjects] = useState<EditableProject[]>([]);

	const [isPublished, setIsPublished] = useState(false);
	const [isVerified, setIsVerified] = useState(false);
	const [slug, setSlug] = useState<string | undefined>();
	const [selectedTemplate, setSelectedTemplate] =
		useState<PortfolioTemplate>("template_a");
	const [liveTemplate, setLiveTemplate] =
		useState<PortfolioTemplate>("template_a");

	const shareUrl = useMemo(
		() => (slug ? `${window.location.origin}/contractor-profile/${slug}` : ""),
		[slug],
	);

	const tradeOptions = useMemo(
		() =>
			TRADES.map((trade) => ({ value: trade.value, label: t(trade.labelKey) })),
		[t],
	);
	const cityOptions = useServiceCities();

	const load = useCallback(async () => {
		setLoading(true);
		setError(false);
		// The service answers null for any failed read. The backend creates the
		// page on first read, so null here always means the request failed.
		const response = await getMyContractorProfile();
		if (!response) {
			setError(true);
			setLoading(false);
			return;
		}
		setCompany({
			name: response.company.name || "",
			logoUrl: response.company.logoUrl || "",
			description: response.company.description || "",
			experienceYears: response.company.experienceYears,
			serviceAreas: response.company.serviceAreas || [],
			specialisations: response.company.specialisations || [],
		});
		setTeamMembers(response.teamMembers.map(createEditableTeamMember));
		setProjects(response.projects.map(createEditableProject));
		setIsPublished(response.profile.isPublished);
		setIsVerified(Boolean(response.contractor?.isVerified));
		setSlug(response.profile.slug);
		const normalized = normalizeContractorTemplate(
			response.profile.template as ContractorProfileTemplate,
		);
		setSelectedTemplate(normalized);
		setLiveTemplate(normalized);
		setLoading(false);
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	function updateCompany(patch: Partial<ContractorCompany>) {
		setCompany((prev) => ({ ...prev, ...patch }));
	}

	// Edits address a card by its key, not its position: an upload that
	// finishes after the list has changed must still land on its own card.
	function updateProject(
		clientKey: string,
		patch: (project: EditableProject) => Partial<ContractorProfileProject>,
	) {
		setProjects((prev) =>
			prev.map((project) =>
				project.clientKey === clientKey
					? { ...project, ...patch(project) }
					: project,
			),
		);
	}

	function moveProject(index: number, direction: "up" | "down") {
		setProjects((prev) => {
			const swapWith = direction === "up" ? index - 1 : index + 1;
			if (swapWith < 0 || swapWith >= prev.length) return prev;
			const next = [...prev];
			[next[index], next[swapWith]] = [next[swapWith], next[index]];
			return next;
		});
	}

	function updateTeamMember(
		clientKey: string,
		patch: Partial<ContractorTeamMember>,
	) {
		setTeamMembers((prev) =>
			prev.map((member) =>
				member.clientKey === clientKey ? { ...member, ...patch } : member,
			),
		);
	}

	// A successful save replaces the project list with the server's copy, so
	// saving waits for every upload to land.
	const [uploadsInFlight, setUploadsInFlight] = useState(0);
	const trackUpload = useCallback((uploading: boolean) => {
		setUploadsInFlight((count) => Math.max(0, count + (uploading ? 1 : -1)));
	}, []);

	/** Saves what is on screen. Resolves true when the server accepted it. */
	async function save(): Promise<boolean> {
		// An entry added and never filled in is dropped rather than refused, and
		// dropped from the screen before sending, so the backend's "Project #2"
		// is the second project on screen.
		const keptMembers = teamMembers.filter((member) => !isBlankMember(member));
		const keptProjects = projects.filter((project) => !isBlankProject(project));
		setTeamMembers(keptMembers);
		setProjects(keptProjects);

		setSaving(true);
		const result = await saveMyContractorProfile({
			template: selectedTemplate as ContractorProfileTemplate,
			company,
			teamMembers: keptMembers.map(
				({ clientKey: _clientKey, ...member }) => member,
			),
			projects: keptProjects.map(({ clientKey: _clientKey, ...project }) => ({
				...project,
				// The first photo is the cover.
				thumbnailUrl: project.imageUrls[0] ?? "",
			})),
		});
		setSaving(false);

		if (!result.success) {
			toast.error(result.error || t("contractorProfile.saveFailed"));
			return false;
		}
		setLiveTemplate(selectedTemplate);
		// The server re-creates projects with new ids. Saving waits for uploads
		// to land and the editor is disabled while it runs, so neither a photo
		// nor a keystroke can arrive in between and be lost here.
		if (result.projects) {
			setProjects(result.projects.map(createEditableProject));
		}
		return true;
	}

	async function handleSave() {
		if (await save()) toast.success(t("contractorProfile.saved"));
	}

	async function handlePublish() {
		if (!(await save())) return;
		setPublishing(true);
		const result = await publishMyContractorProfile();
		setPublishing(false);

		if (!result.success) {
			toast.error(result.error || t("contractorProfile.publishFailed"));
			return;
		}
		setIsPublished(true);
		if (result.shareUrlPath) {
			setSlug(result.shareUrlPath.split("/").pop());
		}
		toast.success(t("contractorProfile.published"));
	}

	async function handleUnpublish() {
		setPublishing(true);
		const result = await unpublishMyContractorProfile();
		setPublishing(false);

		if (!result.success) {
			toast.error(result.error || t("contractorProfile.unpublishFailed"));
			return;
		}
		setIsPublished(false);
		toast.success(t("contractorProfile.unpublished"));
	}

	async function copyShareLink() {
		if (!shareUrl) return;
		try {
			await navigator.clipboard.writeText(shareUrl);
			toast.success(t("contractorProfile.linkCopied"));
		} catch {
			// Clipboard access needs a secure context and a permission grant.
			toast.error(t("contractorProfile.linkCopyFailed"));
		}
	}

	async function handleApplyTemplate() {
		const ok = await save();
		if (ok) {
			toast.success(
				isPublished
					? t(
							"contractorProfile.templateApplied",
							"Template applied to public portfolio",
						)
					: t("contractorProfile.templateSaved", "Template saved"),
			);
		}
	}

	const activeSelectedTemplateDef = useMemo(
		() => getPortfolioTemplate(selectedTemplate),
		[selectedTemplate],
	);
	const activeLiveTemplateDef = useMemo(
		() => getPortfolioTemplate(liveTemplate),
		[liveTemplate],
	);

	if (loading) {
		return (
			<div
				className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6 lg:py-10"
				data-testid="contractor-profile-loading"
			>
				<Skeleton className="h-10 w-80" />
				<Skeleton className="h-80 w-full rounded-2xl" />
				<Skeleton className="h-40 w-full rounded-2xl" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:py-10">
				<FloatPanel elevation={1} padding={24}>
					<QueryErrorState
						title={t("contractorProfile.loadErrorTitle")}
						message={t("contractorProfile.loadErrorBody")}
						onRetry={() => void load()}
					/>
				</FloatPanel>
			</div>
		);
	}

	const busy = saving || publishing;
	const uploading = uploadsInFlight > 0;
	const readyProjects = projects.filter((project) => !isBlankProject(project));

	return (
		<div className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6 lg:py-10">
			<PageHeader
				title={company.name.trim() || t("contractorProfile.untitled")}
				description={
					<span className="flex flex-wrap items-center gap-2">
						<Pill tone={isPublished ? "done" : "neutral"} dot>
							{isPublished
								? t("contractorProfile.statusLive")
								: t("contractorProfile.statusDraft")}
						</Pill>
						<Pill tone={isPublished ? "done" : "neutral"} dot>
							{isPublished
								? `${t("portfolio.publicTemplate", "Public Template:")} ${activeLiveTemplateDef.name}`
								: `${t("portfolio.savedTemplateLabel", "Saved Template:")} ${activeLiveTemplateDef.name}`}
						</Pill>
						<span>
							{isPublished
								? t("contractorProfile.liveHint")
								: t("contractorProfile.draftHint")}
						</span>
					</span>
				}
				actions={
					<>
						{shareUrl ? (
							<StudioButton
								variant="secondary"
								icon={ExternalLink}
								onClick={() =>
									window.open(shareUrl, "_blank", "noopener,noreferrer")
								}
							>
								View Public Portfolio
							</StudioButton>
						) : null}
						<StudioButton
							variant="secondary"
							onClick={() => void handleSave()}
							disabled={busy || uploading}
						>
							{uploading
								? t("contractorProfile.waitingForUploads")
								: saving && !publishing
									? t("contractorProfile.saving")
									: t("contractorProfile.save")}
						</StudioButton>
						{isPublished ? (
							<StudioButton
								variant="quiet"
								onClick={() => void handleUnpublish()}
								disabled={busy}
							>
								{t("contractorProfile.unpublish")}
							</StudioButton>
						) : (
							<StudioButton
								variant="primary"
								icon={Rocket}
								onClick={() => void handlePublish()}
								disabled={busy || uploading}
							>
								{publishing
									? t("contractorProfile.publishing")
									: t("contractorProfile.publish")}
							</StudioButton>
						)}
					</>
				}
			/>

			{isPublished && shareUrl ? (
				<FloatPanel elevation={1} padding={16}>
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<p className="min-w-0 flex-1 truncate font-body text-sm text-dashboard-muted-dark">
							<span className="sr-only">
								{t("contractorProfile.publicLink")}:{" "}
							</span>
							{shareUrl}
						</p>
						<div className="flex gap-2">
							<StudioButton
								variant="secondary"
								size="sm"
								icon={Copy}
								onClick={() => void copyShareLink()}
							>
								{t("contractorProfile.copyLink")}
							</StudioButton>
							<StudioButton
								variant="secondary"
								size="sm"
								icon={ExternalLink}
								onClick={() =>
									window.open(shareUrl, "_blank", "noopener,noreferrer")
								}
							>
								{t("contractorProfile.openLink")}
							</StudioButton>
						</div>
					</div>
				</FloatPanel>
			) : null}

			<fieldset
				disabled={busy}
				aria-busy={busy}
				className="m-0 min-w-0 space-y-6 border-0 p-0"
			>
				<SectionPanel
					title={t("contractorProfile.company.title")}
					description={t("contractorProfile.company.description")}
				>
					<div className="grid gap-6 sm:grid-cols-[auto_1fr]">
						<Field label={t("contractorProfile.company.logo")}>
							<SingleImageUpload
								label=""
								currentUrl={company.logoUrl}
								onUploadingChange={trackUpload}
								onUpload={(url) => updateCompany({ logoUrl: url })}
								onRemove={() => updateCompany({ logoUrl: "" })}
							/>
						</Field>
						<div className="grid content-start gap-5 sm:grid-cols-[1fr_140px]">
							<Field
								label={t("contractorProfile.company.name")}
								htmlFor="company-name"
							>
								<input
									id="company-name"
									type="text"
									value={company.name}
									onChange={(e) => updateCompany({ name: e.target.value })}
									className={INPUT_CLASS}
								/>
							</Field>
							<Field
								label={t("contractorProfile.company.years")}
								htmlFor="company-years"
							>
								<YearsInput
									id="company-years"
									value={company.experienceYears}
									onChange={(years) =>
										updateCompany({ experienceYears: years })
									}
								/>
							</Field>
							<Field
								label={t("contractorProfile.company.about")}
								htmlFor="company-about"
								className="sm:col-span-2"
							>
								<textarea
									id="company-about"
									value={company.description ?? ""}
									onChange={(e) =>
										updateCompany({ description: e.target.value })
									}
									placeholder={t("contractorProfile.company.aboutPlaceholder")}
									className={TEXTAREA_CLASS}
								/>
							</Field>
						</div>
					</div>

					<div className="mt-8 space-y-8 border-t border-dashboard-divider pt-6">
						<ChipPicker
							legend={t("contractorProfile.company.trades")}
							options={tradeOptions}
							selected={company.specialisations}
							onChange={(specialisations) => updateCompany({ specialisations })}
							addPlaceholder={t("contractorProfile.company.addTrade")}
						/>
						<ChipPicker
							legend={t("contractorProfile.company.areas")}
							options={cityOptions}
							selected={company.serviceAreas}
							onChange={(serviceAreas) => updateCompany({ serviceAreas })}
							addPlaceholder={t("contractorProfile.company.addArea")}
							normalise={titleCase}
						/>
					</div>
				</SectionPanel>

				<SectionPanel
					title={t(
						"contractorProfile.template.title",
						"Portfolio Template & Public Presentation",
					)}
					description={t(
						"contractorProfile.template.description",
						"Select how your public portfolio and projects appear to clients. Each template applies custom typography, brand wordmarks, layout ribbons, and project structures.",
					)}
				>
					<div className="space-y-6">
						{/* Upper Row: Status and Template Selector */}
						<div className="space-y-4">
							<div className="flex flex-wrap items-center justify-between gap-3">
								<p className="font-display text-sm font-semibold text-dashboard-text">
									{t("portfolio.chooseTemplate", "Choose Template")}
								</p>
							</div>

							{/* Upper Row Template Selector */}
							<TemplateSwitcher
								selectedTemplate={selectedTemplate}
								savedTemplate={liveTemplate}
								onSelectTemplate={setSelectedTemplate}
								isPublished={isPublished}
								dataTestId="contractor-template-switcher"
							/>

							<TemplateDeltaNotice
								selectedTemplate={selectedTemplate}
								savedTemplate={liveTemplate}
								onApply={() => void handleApplyTemplate()}
								isSaving={saving}
							/>
						</div>

						{/* Live Preview Direct on Screen */}
						<div className="space-y-4 pt-2">
							<div className="flex items-center justify-between">
								<div>
									<p className="font-display text-sm font-semibold text-dashboard-text">
										{t("portfolio.livePreview", "Live Preview")}:{" "}
										{activeSelectedTemplateDef.name}
									</p>
									<p className="text-xs text-dashboard-muted">
										{activeSelectedTemplateDef.changesSummary}
									</p>
								</div>
								{selectedTemplate !== liveTemplate && (
									<StudioButton
										variant="primary"
										size="sm"
										onClick={() => void handleApplyTemplate()}
										disabled={busy || uploading}
									>
										{isPublished
											? t(
													"portfolio.applyToPublic",
													"Apply to Public Portfolio",
												)
											: t("portfolio.saveTemplate", "Save Template")}
									</StudioButton>
								)}
							</div>

							<div className="overflow-hidden rounded-xl border border-dashboard-border shadow-xs">
								<div className={activeSelectedTemplateDef.pageWrapperClass}>
									{renderPublicTemplateHeader(selectedTemplate, {
										designer: {
											name: company.name.trim() || "Contractor",
											firmName: company.name.trim() || undefined,
											profilePictureUrl: company.logoUrl || undefined,
											bio: company.description || undefined,
											isVerified,
										},
										projectCount: projects.filter((p) => !isBlankProject(p))
											.length,
										signedIn: true,
										templateName: activeSelectedTemplateDef.name,
										templateSummary: activeSelectedTemplateDef.changesSummary,
									})}
									<div className="p-4 sm:p-6">
										{activeSelectedTemplateDef.LivePreview({
											projects: projects
												.filter((p) => !isBlankProject(p))
												.map((p) => ({
													id: p.clientKey,
													title: p.title || "Project",
													shortDescription: p.shortDescription || "",
													thumbnailUrl: p.thumbnailUrl || p.imageUrls?.[0],
												})),
											mode: "preview",
										})}
									</div>
								</div>
							</div>
						</div>
					</div>
				</SectionPanel>

				<SectionPanel
					title={t("contractorProfile.projects.title")}
					description={t("contractorProfile.projects.description")}
					action={
						<StudioButton
							variant="secondary"
							size="sm"
							icon={Plus}
							onClick={() =>
								setProjects((prev) => [...prev, createEditableProject()])
							}
						>
							{t("contractorProfile.projects.add")}
						</StudioButton>
					}
				>
					{projects.length === 0 ? (
						<p className="font-body text-sm text-dashboard-muted">
							{isPublished
								? t("contractorProfile.projects.empty")
								: t("contractorProfile.projects.neededToPublish")}
						</p>
					) : (
						<ol className="space-y-5">
							{projects.map((project, index) => (
								<li
									key={project.clientKey}
									data-testid={`contractor-project-card-${index}`}
									className="rounded-2xl border border-dashboard-border bg-dashboard-warm-150 p-4 sm:p-5"
								>
									<div className="mb-4 flex items-center justify-between gap-3">
										<p className="font-body text-sm font-semibold text-dashboard-muted-dark">
											{project.title.trim() ||
												t("contractorProfile.projects.untitled", {
													number: index + 1,
												})}
										</p>
										<div className="flex items-center gap-1.5">
											<StudioButton
												variant="ghost"
												size="sm"
												onClick={() => moveProject(index, "up")}
												disabled={index === 0}
												aria-label={t("contractorProfile.projects.moveUp", {
													number: index + 1,
												})}
											>
												<ArrowUp className="h-4 w-4" />
											</StudioButton>
											<StudioButton
												variant="ghost"
												size="sm"
												onClick={() => moveProject(index, "down")}
												disabled={index === projects.length - 1}
												aria-label={t("contractorProfile.projects.moveDown", {
													number: index + 1,
												})}
											>
												<ArrowDown className="h-4 w-4" />
											</StudioButton>
											<StudioButton
												variant="ghost"
												size="sm"
												onClick={() =>
													setProjects((prev) =>
														prev.filter((_, i) => i !== index),
													)
												}
												aria-label={t("contractorProfile.projects.remove", {
													number: index + 1,
												})}
											>
												<Trash2 className="h-4 w-4" />
											</StudioButton>
										</div>
									</div>
									<div className="grid gap-4">
										<Field
											label={t("contractorProfile.projects.name")}
											htmlFor={`project-title-${project.clientKey}`}
										>
											<input
												id={`project-title-${project.clientKey}`}
												type="text"
												value={project.title}
												placeholder={t(
													"contractorProfile.projects.namePlaceholder",
												)}
												onChange={(e) =>
													updateProject(project.clientKey, () => ({
														title: e.target.value,
													}))
												}
												className={INPUT_CLASS}
											/>
										</Field>
										<Field
											label={t("contractorProfile.projects.whatYouDid")}
											htmlFor={`project-description-${project.clientKey}`}
										>
											<textarea
												id={`project-description-${project.clientKey}`}
												value={project.shortDescription}
												onChange={(e) =>
													updateProject(project.clientKey, () => ({
														shortDescription: e.target.value,
													}))
												}
												className={TEXTAREA_CLASS}
											/>
										</Field>
										<Field
											label={t("contractorProfile.projects.photos")}
											hint={t("contractorProfile.projects.photosHint")}
										>
											<MultiImageUpload
												label=""
												currentUrls={project.imageUrls}
												onUploadingChange={trackUpload}
												onUpload={(urls) =>
													updateProject(project.clientKey, (current) => ({
														imageUrls: [...current.imageUrls, ...urls],
													}))
												}
												onRemove={(imageIndex) =>
													updateProject(project.clientKey, (current) => ({
														imageUrls: current.imageUrls.filter(
															(_, i) => i !== imageIndex,
														),
													}))
												}
											/>
										</Field>
									</div>
								</li>
							))}
						</ol>
					)}
					{!isPublished && projects.length > 0 && readyProjects.length === 0 ? (
						<p className="mt-4 font-body text-[13px] text-dashboard-muted-dark">
							{t("contractorProfile.projects.neededToPublish")}
						</p>
					) : null}
				</SectionPanel>

				<SectionPanel
					title={t("contractorProfile.team.title")}
					description={t("contractorProfile.team.description")}
					action={
						<StudioButton
							variant="secondary"
							size="sm"
							icon={Plus}
							onClick={() =>
								setTeamMembers((prev) => [...prev, createEditableTeamMember()])
							}
						>
							{t("contractorProfile.team.add")}
						</StudioButton>
					}
				>
					{teamMembers.length === 0 ? (
						<p className="font-body text-sm text-dashboard-muted">
							{t("contractorProfile.team.empty")}
						</p>
					) : (
						<ul className="space-y-5">
							{teamMembers.map((member, index) => (
								<li
									key={member.clientKey}
									className="rounded-2xl border border-dashboard-border bg-dashboard-warm-150 p-4 sm:p-5"
								>
									<div className="grid gap-5 sm:grid-cols-[auto_1fr]">
										<Field label={t("contractorProfile.team.photo")}>
											<SingleImageUpload
												label=""
												currentUrl={member.profilePhotoUrl}
												onUploadingChange={trackUpload}
												onUpload={(url) =>
													updateTeamMember(member.clientKey, {
														profilePhotoUrl: url,
													})
												}
												onRemove={() =>
													updateTeamMember(member.clientKey, {
														profilePhotoUrl: "",
													})
												}
											/>
										</Field>
										<div className="grid content-start gap-4 sm:grid-cols-[1fr_1fr_110px]">
											<Field
												label={t("contractorProfile.team.name")}
												htmlFor={`member-name-${member.clientKey}`}
											>
												<input
													id={`member-name-${member.clientKey}`}
													type="text"
													value={member.name}
													onChange={(e) =>
														updateTeamMember(member.clientKey, {
															name: e.target.value,
														})
													}
													className={INPUT_CLASS}
												/>
											</Field>
											<Field
												label={t("contractorProfile.team.role")}
												htmlFor={`member-role-${member.clientKey}`}
											>
												<input
													id={`member-role-${member.clientKey}`}
													type="text"
													value={member.role}
													placeholder={t(
														"contractorProfile.team.rolePlaceholder",
													)}
													onChange={(e) =>
														updateTeamMember(member.clientKey, {
															role: e.target.value,
														})
													}
													className={INPUT_CLASS}
												/>
											</Field>
											<Field
												label={t("contractorProfile.team.years")}
												htmlFor={`member-years-${member.clientKey}`}
											>
												<YearsInput
													id={`member-years-${member.clientKey}`}
													value={member.experienceYears}
													onChange={(years) =>
														updateTeamMember(member.clientKey, {
															experienceYears: years,
														})
													}
												/>
											</Field>
											<Field
												label={t("contractorProfile.team.bio")}
												htmlFor={`member-bio-${member.clientKey}`}
												className="sm:col-span-3"
											>
												<textarea
													id={`member-bio-${member.clientKey}`}
													value={member.shortBio ?? ""}
													onChange={(e) =>
														updateTeamMember(member.clientKey, {
															shortBio: e.target.value,
														})
													}
													className={TEXTAREA_CLASS}
												/>
											</Field>
										</div>
									</div>
									<div className="mt-3 flex justify-end">
										<StudioButton
											variant="ghost"
											size="sm"
											icon={Trash2}
											onClick={() =>
												setTeamMembers((prev) =>
													prev.filter((_, i) => i !== index),
												)
											}
										>
											{t("contractorProfile.team.remove")}
										</StudioButton>
									</div>
								</li>
							))}
						</ul>
					)}
				</SectionPanel>
			</fieldset>
		</div>
	);
}
